import { format } from '@seek/currency';
import type { SalaryCurrency } from '@seek/salary-data/lib-types/types';
import type { useTranslations } from '@vocab/react';

import { LANGUAGE } from 'src/app/shared/constants/configObjectProperties';
import { LANGUAGE_LOCALE_DEFAULT } from 'src/app/shared/constants/language';
import { getConfig } from 'src/app/utils/config';

import type { GetMinMaxAndAllowablesResult } from './getMinMaxAndAllowables';

export type Translator = ReturnType<typeof useTranslations>['t'];

const locale = getConfig(LANGUAGE, LANGUAGE_LOCALE_DEFAULT);

export const getValidationTranslations = (
  t: Translator,
  {
    allowableMin,
    allowableMax,
    minAllowableSalaryText,
    maxAllowableSalaryText,
  }: GetMinMaxAndAllowablesResult,
  currency: SalaryCurrency,
) => {
  const tPayRangeValidationMinReq = t('pay-range-validation-minimum-required');
  const tValMinGreaterThanMinAllowable = t(
    'pay-range-validation-minimum-must-be-greater-than-min-allowable',
    {
      minAllowableSalary: minAllowableSalaryText,
    },
  ) as string;
  const tValMinLessThanMaxAllowable = t(
    'pay-range-validation-minimum-must-be-less-than-max-allowable',
    {
      maxAllowableSalary: maxAllowableSalaryText,
    },
  ) as string;
  const tValMaxRequired = t('pay-range-validation-maximum-required');
  const tValMaxGreaterThanMinAllowable = t(
    'pay-range-validation-maximum-must-be-greater-than-min-allowable',
    {
      minAllowableSalary: minAllowableSalaryText,
    },
  ) as string;
  const tValMaxLesserThanMaxAllowable = t(
    'pay-range-validation-maximum-must-be-less-than-max-allowable',
    {
      maxAllowableSalary: maxAllowableSalaryText,
    },
  ) as string;
  const tValMaxGreaterThanMin = t('pay-range-validation-max-greater-than-min');
  const tValMinMaxErrorMsg = t('pay-range-validation-allowable-range', {
    allowableMin: format(allowableMin, {
      currency,
      locale,
      dropFraction: true,
    }),
    allowableMax: format(allowableMax, {
      currency,
      locale,
      dropFraction: true,
    }),
  }) as string;

  return {
    tPayRangeValidationMinReq,
    tValMinGreaterThanMinAllowable,
    tValMinLessThanMaxAllowable,
    tValMaxRequired,
    tValMaxLesserThanMaxAllowable,
    tValMaxGreaterThanMinAllowable,
    tValMaxGreaterThanMin,
    tValMinMaxErrorMsg,
  };
};

export type ValidationTranslations = ReturnType<
  typeof getValidationTranslations
>;
